import { EMPTY, Observable, catchError } from 'rxjs';

/**
 * Stops one failed fetch from permanently killing a refreshable page.
 *
 * Both the event-day and manage-rounds pages are built as `paramMap -> refresh$ -> fetch`.
 * An error from the fetch propagates out and COMPLETES the outer subscription, after which
 * no later refresh$ emission is ever seen again -- the page is dead until it is recreated,
 * and its refresh button does nothing. Since a refresh button is precisely what gets
 * pressed when something has already gone wrong, that is the worst place to lose the
 * stream.
 *
 * Apply this to the INNER observable (the fetch), never to the outer pipe, where it would
 * swallow the error only after the damage is done:
 *
 *     switchMap(() => this.eventService.getRounds(id).pipe(surviveRefreshError(() => this.failed())))
 *
 * Emits EMPTY rather than a fallback value, so the caller's signals keep whatever they
 * last held instead of the page blanking itself out mid-event.
 *
 * @param handle Called on failure -- clear any in-flight flag and tell the user. Kept as a
 *               callback so each page owns its own message and state rather than this
 *               file depending on the toast and translation services.
 */
export function surviveRefreshError<T>(handle: () => void): (source: Observable<T>) => Observable<T> {
    return (source: Observable<T>) =>
        source.pipe(
            catchError(() => {
                handle();
                return EMPTY;
            })
        );
}
